import React from 'react';
import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import { Headphones, GraduationCap, ClipboardCheck, FileText, ChevronRight, BookOpen, Shuffle, FileSpreadsheet, Brain, Edit3, Trophy } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import EditableText from '@/components/EditableText';
import EditableButton from '@/components/EditableButton';
import PageQuote from '@/components/PageQuote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentResources() {
  const heroImage = useHeroImage('student_resources');

  const resources = [
    {
      id: 'flashcards',
      title: 'Flashcards',
      description: 'Master key concepts with interactive flashcards organized by tier and category.',
      icon: BookOpen,
      link: '/flashcards-center',
      color: 'from-purple-600 to-purple-800',
      bgPattern: 'bg-[radial-gradient(circle_at_30%_40%,rgba(147,51,234,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'matching',
      title: 'Matching Games',
      description: 'Test your knowledge by matching terms with definitions in engaging challenges.',
      icon: Shuffle,
      link: '/matching-center',
      color: 'from-blue-600 to-blue-800',
      bgPattern: 'bg-[radial-gradient(circle_at_70%_30%,rgba(37,99,235,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'study-guides',
      title: 'Study Guides',
      description: 'Access comprehensive study guides with organized content and key concepts.',
      icon: FileSpreadsheet,
      link: '/study-guide-center',
      color: 'from-green-600 to-green-800',
      bgPattern: 'bg-[radial-gradient(circle_at_60%_70%,rgba(22,163,74,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'memory-game',
      title: 'Memory Game',
      description: 'Match terms with definitions in a fun memory card game. Beat your best time!',
      icon: Brain,
      link: '/memory-game-center',
      color: 'from-pink-600 to-red-600',
      bgPattern: 'bg-[radial-gradient(circle_at_40%_60%,rgba(236,72,153,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'fill-in',
      title: 'Fill In',
      description: 'Practice fill-in-the-blank exercises. AI-graded by Ernie.AI with printable sheets.',
      icon: Edit3,
      link: '/fill-in-center',
      color: 'from-blue-500 to-blue-700',
      bgPattern: 'bg-[radial-gradient(circle_at_30%_30%,rgba(59,130,246,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'jeopardy',
      title: 'Jeopardy Game',
      description: 'Answer in question form and score points! Compete for the Legend of the Internet title.',
      icon: Trophy,
      link: '/jeopardy-center',
      color: 'from-orange-500 to-yellow-600',
      bgPattern: 'bg-[radial-gradient(circle_at_70%_40%,rgba(249,115,22,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'publication-center',
      title: 'Publication Center',
      description: 'Access professional publications and research materials. Purchase individually or subscribe for unlimited access.',
      icon: BookOpen,
      link: '/publication-center',
      color: 'from-amber-700 to-amber-900',
      bgPattern: 'bg-[radial-gradient(circle_at_50%_50%,rgba(180,83,9,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'blog',
      title: 'Student Blog',
      description: 'Follow the journey of learning acupuncture and TCM through personal insights and experiences.',
      icon: FileText,
      link: '/student-blog',
      color: 'from-[#D4A574] to-[#B4956A]',
      bgPattern: 'bg-[radial-gradient(circle_at_10%_20%,rgba(212,165,116,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'classes',
      title: 'Classes',
      description: 'Explore comprehensive classes with structured lessons, study materials, and expert instruction.',
      icon: GraduationCap,
      link: '/class-center',
      color: 'from-emerald-600 to-teal-700',
      bgPattern: 'bg-[radial-gradient(circle_at_20%_50%,rgba(16,185,129,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'courses',
      title: 'Courses',
      description: 'Explore comprehensive courses covering acupuncture techniques, theory, and clinical applications.',
      icon: GraduationCap,
      link: '/courses',
      color: 'from-[#E8934D] to-[#C8833D]',
      bgPattern: 'bg-[radial-gradient(circle_at_80%_30%,rgba(232,147,77,0.3)_0%,transparent_50%)]'
    },
    {
      id: 'testing',
      title: 'Testing Center',
      description: 'Test your knowledge and track your progress with our comprehensive testing platform.',
      icon: ClipboardCheck,
      link: '/testing-center',
      color: 'from-[#E8B67A] to-[#C8A65A]',
      bgPattern: 'bg-[radial-gradient(circle_at_50%_80%,rgba(232,182,122,0.3)_0%,transparent_50%)]'
    }
  ];

  return (
    <Layout>
      {/* Hero Section - Standardized Height */}
      <section className="relative vhs-effect overflow-hidden">
        <HeroImageEditor pageName="student_resources" />
        <div className="absolute inset-0 z-0 h-[400px]">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-40"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: 'sepia(40%) saturate(120%) hue-rotate(-5deg) brightness(0.9)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#2C1810]/80 via-[#E85D4D]/10 to-[#2C1810]/80"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-[400px] flex items-center justify-center">
          <div className="text-center space-y-8">
            <h1 className="text-6xl sm:text-7xl md:text-8xl retro-title leading-tight">
              <EditableText as="span" id="student_resources.hero.title">Student Resources</EditableText>
            </h1>
            <p className="text-xl md:text-2xl text-[#F4E4C1] max-w-3xl mx-auto font-medium">
              <EditableText as="span" id="student_resources.hero.subtitle">Your hub for blog posts, classes, learning materials, and testing tools</EditableText>
            </p>
          </div>
        </div>
        
        {/* Decorative VHS lines */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8B67A] to-transparent opacity-30 z-20"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8B67A] to-transparent opacity-30 z-20"></div>
      </section>

      {/* Quote Position 1 - Above Main Content */}
      <PageQuote pageName="student_resources" position={1} variant="compact" />

      {/* Resources Grid */}
      <section className="py-20 bg-gradient-to-b from-[#1A0F0A] to-[#2C1810]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {resources.map((resource) => {
              const Icon = resource.icon;
              return (
                <Link 
                  key={resource.id}
                  to={resource.link}
                  className="group relative"
                >
                  <div className="relative h-full retro-card p-8 overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:rotate-1">
                    {/* Background Pattern */}
                    <div className={`absolute inset-0 ${resource.bgPattern} opacity-50`}></div>
                    
                    {/* Decorative Corners */}
                    <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-[#E8934D] opacity-60"></div>
                    <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-[#E8934D] opacity-60"></div>
                    
                    {/* Content */}
                    <div className="relative z-10 space-y-6">
                      {/* Icon with Animation */}
                      <div className={`inline-flex p-6 rounded-2xl bg-gradient-to-br ${resource.color} shadow-2xl transform transition-transform hover:rotate-12 hover:scale-110 border-4 border-[#F4E4C1]`}>
                        <Icon className="w-16 h-16 text-[#F4E4C1] drop-shadow-lg" strokeWidth={2.5} />
                      </div>
                      
                      {/* Title */}
                      <h2 className="text-3xl font-bold text-[#F4E4C1] group-hover:text-[#E8934D] transition-colors">
                        {resource.title}
                      </h2>
                      
                      {/* Description */}
                      <p className="text-lg text-[#E8B67A] leading-relaxed">
                        {resource.description}
                      </p>
                      
                      {/* Arrow */}
                      <div className="flex items-center text-[#E8934D] font-semibold group-hover:translate-x-2 transition-transform">
                        <span className="mr-2">Explore</span>
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </div>
                    
                    {/* Decorative corner */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#E8934D]/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-[#E85D4D]/20 to-transparent"></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-[#2C1810] via-[#E85D4D]/20 to-[#2C1810]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl retro-title mb-6">
            <EditableText as="span" id="student_resources.cta.title">Ready to Learn?</EditableText>
          </h2>
          <p className="text-xl text-[#F4E4C1] mb-8">
            <EditableText as="span" id="student_resources.cta.subtitle">Dive into our comprehensive collection of educational resources</EditableText>
          </p>
          <EditableButton id="student_resources.cta.button" defaultLabel="Browse All Courses" defaultUrl="/courses" className="retro-button inline-block" />
        </div>
      </section>

      {/* Quote Position 2 - Below Main Content */}
      <PageQuote pageName="student_resources" position={2} variant="compact" />
    </Layout>
  );
}
